import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePWA } from "../hooks/usePWA";
import Icon from "./Icon";

const PWAInstallPrompt = () => {
  const { churchUser } = useAuth();
  const {
    canInstall,
    isInstalled,
    installPWA,
    requestNotificationPermission,
    needsNotificationPermission,
    sendLocalNotification,
    sendTestNotification,
    subscribeToTopic,
  } = usePWA();

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [lastPromptTime, setLastPromptTime] = useState(null);
  const [promptCount, setPromptCount] = useState(0);

  useEffect(() => {
    if (!canInstall || isInstalled) {
      setShowInstallPrompt(false);
      return;
    }

    const storedData = localStorage.getItem("pwa-install-prompt");
    const data = storedData ? JSON.parse(storedData) : {};

    const now = Date.now();
    const lastPrompt = data.lastPrompt || 0;
    const count = data.count || 0;
    const dismissed = data.dismissed || false;

    if (dismissed && now - lastPrompt < 24 * 60 * 60 * 1000) return;

    if (count === 0) {
      setShowInstallPrompt(true);
      setPromptCount(1);
      setLastPromptTime(now);
      return;
    }

    const fifteenMinutes = 15 * 60 * 1000;
    const shouldShow = now - lastPrompt >= fifteenMinutes && count < 3;

    if (shouldShow) {
      const timer = setTimeout(() => {
        setShowInstallPrompt(true);
        setPromptCount(count + 1);
        setLastPromptTime(now);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [canInstall, isInstalled]);

  useEffect(() => {
    if (!churchUser) { setShowNotificationPrompt(false); return; }
    if (!needsNotificationPermission || showInstallPrompt) return;
    const timer = setTimeout(() => setShowNotificationPrompt(true), 5000);
    return () => clearTimeout(timer);
  }, [needsNotificationPermission, showInstallPrompt, churchUser]);

  useEffect(() => {
    if (lastPromptTime && promptCount > 0) {
      localStorage.setItem("pwa-install-prompt", JSON.stringify({
        lastPrompt: lastPromptTime,
        count: promptCount,
        dismissed: false,
      }));
    }
  }, [lastPromptTime, promptCount]);

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      const success = await installPWA();
      if (success) {
        setShowInstallPrompt(false);
        sendLocalNotification("Igreja App Instalado!", {
          body: "O aplicativo foi instalado com sucesso. Agora você pode acessá-lo diretamente da sua tela inicial.",
          tag: "install-success",
        });
      }
    } catch (error) {
      console.error("Erro durante instalação:", error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
    localStorage.setItem("pwa-install-prompt", JSON.stringify({
      lastPrompt: Date.now(),
      count: promptCount,
      dismissed: true,
    }));
  };

  const handleNotificationPermission = async () => {
    const userId = churchUser?.uid || churchUser?.membro_id || churchUser?.id;
    if (!userId) { alert("Erro: ID do usuário não disponível. Faça login novamente."); return; }
    try {
      const token = await requestNotificationPermission(userId);
      if (token) {
        setShowNotificationPrompt(false);
        try { await subscribeToTopic(userId, "all_members"); } catch {}
        setTimeout(async () => { try { await sendTestNotification(userId); } catch {} }, 2000);
        sendLocalNotification("Notificações Ativadas!", {
          body: "Você receberá notificações importantes sobre a igreja.",
          tag: "notification-enabled",
        });
      } else {
        alert("Não foi possível ativar as notificações. Verifique as configurações do navegador.");
      }
    } catch (error) {
      alert(`Erro ao ativar notificações: ${error.message}`);
    }
  };

  const benefitItem = (icon, color, text) => (
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
      <div style={{
        width: 32, height: 32, borderRadius: "var(--radius-md)", flexShrink: 0,
        backgroundColor: `${color}18`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon name={icon} size={15} color={color} />
      </div>
      <span style={{ fontSize: "0.85rem", color: "var(--color-text)" }}>{text}</span>
    </div>
  );

  return (
    <>
      {/* ── Modal Instalar App ── */}
      {showInstallPrompt && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
          padding: "1rem",
        }}>
          <div style={{
            width: "100%", maxWidth: 420,
            backgroundColor: "var(--color-surface)",
            borderRadius: "var(--radius-xl)",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            animation: "slideUp 0.25s ease-out",
          }}>
            {/* Header gradiente */}
            <div style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "1.25rem 1.25rem 1rem",
              position: "relative",
            }}>
              <button
                onClick={handleDismissInstall}
                style={{
                  position: "absolute", top: "0.75rem", right: "0.75rem",
                  background: "rgba(255,255,255,0.2)", border: "none",
                  borderRadius: "50%", width: 28, height: 28,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "white",
                }}
              >
                <Icon name="x" size={14} />
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                <div style={{
                  width: 48, height: 48, borderRadius: "var(--radius-lg)",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}>
                  <Icon name="phone" size={24} color="white" />
                </div>
                <div>
                  <div style={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>Instalar App</div>
                  <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.78rem" }}>
                    Acesso rápido na tela inicial
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: "1.25rem" }}>
              <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
                Tenha acesso ao sistema da igreja direto da sua tela inicial. Funciona offline e recebe notificações.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "1.25rem" }}>
                {benefitItem("zap", "#667eea", "Acesso rápido da tela inicial")}
                {benefitItem("wifi-x", "#10b981", "Funciona mesmo offline")}
                {benefitItem("bell-ring", "#f59e0b", "Receba notificações importantes")}
                {benefitItem("bolt", "#8b5cf6", "Carregamento mais rápido")}
              </div>

              <div style={{ display: "flex", gap: "0.6rem" }}>
                <button
                  onClick={handleDismissInstall}
                  style={{
                    flex: 1, padding: "0.6rem",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    background: "transparent",
                    color: "var(--color-text-muted)",
                    fontSize: "0.85rem", fontWeight: 500, cursor: "pointer",
                  }}
                >
                  Agora não
                </button>
                <button
                  onClick={handleInstall}
                  disabled={isInstalling}
                  style={{
                    flex: 1, padding: "0.6rem",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none", borderRadius: "var(--radius-md)",
                    color: "white", fontSize: "0.85rem", fontWeight: 600,
                    cursor: isInstalling ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
                    opacity: isInstalling ? 0.7 : 1,
                  }}
                >
                  <Icon name={isInstalling ? "loader" : "download"} size={15} className={isInstalling ? "spinner" : ""} />
                  {isInstalling ? "Instalando..." : "Instalar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Ativar Notificações ── */}
      {showNotificationPrompt && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9998,
          backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
          padding: "1rem",
        }}>
          <div style={{
            width: "100%", maxWidth: 420,
            backgroundColor: "var(--color-surface)",
            borderRadius: "var(--radius-xl)",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            animation: "slideUp 0.25s ease-out",
          }}>
            {/* Header gradiente âmbar */}
            <div style={{
              background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
              padding: "1.25rem 1.25rem 1rem",
              position: "relative",
            }}>
              <button
                onClick={() => setShowNotificationPrompt(false)}
                style={{
                  position: "absolute", top: "0.75rem", right: "0.75rem",
                  background: "rgba(255,255,255,0.2)", border: "none",
                  borderRadius: "50%", width: 28, height: 28,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "white",
                }}
              >
                <Icon name="x" size={14} />
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                <div style={{
                  width: 48, height: 48, borderRadius: "var(--radius-lg)",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}>
                  <Icon name="bell-ring" size={24} color="white" />
                </div>
                <div>
                  <div style={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>Ativar Notificações</div>
                  <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.78rem" }}>
                    Fique sempre informado
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: "1.25rem" }}>
              <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
                Permita que enviemos notificações sobre eventos importantes, lembretes e atualizações da igreja.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "1.25rem" }}>
                {benefitItem("calendar", "#667eea", "Lembretes de eventos e cultos")}
                {benefitItem("heart", "#ef4444", "Confirmação de contribuições")}
                {benefitItem("message-circle", "#10b981", "Avisos importantes da liderança")}
              </div>

              <div style={{ display: "flex", gap: "0.6rem" }}>
                <button
                  onClick={() => setShowNotificationPrompt(false)}
                  style={{
                    flex: 1, padding: "0.6rem",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    background: "transparent",
                    color: "var(--color-text-muted)",
                    fontSize: "0.85rem", fontWeight: 500, cursor: "pointer",
                  }}
                >
                  Talvez depois
                </button>
                <button
                  onClick={handleNotificationPermission}
                  style={{
                    flex: 1, padding: "0.6rem",
                    background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                    border: "none", borderRadius: "var(--radius-md)",
                    color: "white", fontSize: "0.85rem", fontWeight: 600,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
                  }}
                >
                  <Icon name="bell-ring" size={15} />
                  Ativar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
